import { useCallback, useEffect, useRef, useState } from 'react';

export interface PermissionItem { key: string; label: string; state: 'checking' | 'passed' | 'failed' | 'denied'; detail: string; ok: boolean }

export function usePermissionCheck() {
  const [items, setItems] = useState<PermissionItem[]>([]);
  const [checking, setChecking] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState('');
  const [microphoneId, setMicrophoneId] = useState('');
  const [stream, setStream] = useState<MediaStream>();
  const [audioLevel, setAudioLevel] = useState(0);
  const streamRef = useRef<MediaStream>();

  const run = useCallback(async () => {
    setChecking(true);
    const checks: PermissionItem[] = [];
    const hasMedia = Boolean(navigator.mediaDevices?.getUserMedia);
    checks.push({ key: 'browser', label: 'Browser compatibility', state: hasMedia ? 'passed' : 'failed', ok: hasMedia, detail: `${navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Safari') ? 'Safari' : 'Modern browser'} on ${navigator.userAgent.includes('Windows') ? 'Windows' : navigator.userAgent.includes('Mac') ? 'macOS/iOS' : 'your operating system'}` });
    try {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      const media = await navigator.mediaDevices.getUserMedia({ video: cameraId ? { deviceId: { exact: cameraId } } : true, audio: microphoneId ? { deviceId: { exact: microphoneId } } : true });
      streamRef.current = media;
      setStream(media);
      const available = await navigator.mediaDevices.enumerateDevices();
      setDevices(available);
      const video = media.getVideoTracks()[0];
      const audio = media.getAudioTracks()[0];
      checks.push({ key: 'webcam', label: 'Webcam', state: video ? 'passed' : 'failed', ok: Boolean(video), detail: video?.label || 'Camera stream available' });
      checks.push({ key: 'microphone', label: 'Microphone', state: audio ? 'passed' : 'failed', ok: Boolean(audio), detail: audio?.label || 'Microphone stream available' });
    } catch (error) {
      const denied = error instanceof DOMException && error.name === 'NotAllowedError';
      checks.push({ key: 'webcam', label: 'Webcam', state: denied ? 'denied' : 'failed', ok: false, detail: error instanceof Error ? error.message : 'Camera unavailable' });
      checks.push({ key: 'microphone', label: 'Microphone', state: denied ? 'denied' : 'failed', ok: false, detail: denied ? 'Permission denied' : 'Microphone unavailable' });
    }
    checks.push({ key: 'fullscreen', label: 'Fullscreen support', state: document.fullscreenEnabled ? 'passed' : 'failed', ok: document.fullscreenEnabled, detail: document.fullscreenEnabled ? 'Fullscreen can be requested' : 'Fullscreen is unavailable' });
    checks.push({ key: 'internet', label: 'Internet', state: navigator.onLine ? 'passed' : 'failed', ok: navigator.onLine, detail: navigator.onLine ? 'Browser reports online' : 'Browser reports offline' });
    setItems(checks);
    setChecking(false);
  }, [cameraId, microphoneId]);

  useEffect(() => () => streamRef.current?.getTracks().forEach((track) => track.stop()), []);
  useEffect(() => {
    if (!stream?.getAudioTracks().length) return;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    context.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const timer = window.setInterval(() => { analyser.getByteFrequencyData(data); setAudioLevel(Math.round(data.reduce((sum, value) => sum + value, 0) / data.length / 2.55)); }, 250);
    return () => { window.clearInterval(timer); void context.close(); };
  }, [stream]);

  return { items, checking, allPassed: items.length > 0 && items.every((item) => item.ok), devices, cameraId, microphoneId, setCameraId, setMicrophoneId, stream, audioLevel, retry: run };
}
